import { useMemo, useCallback, type ReactNode } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  TableSortLabel,
  Alert,
  useTheme,
  LinearProgress,
} from '@mui/material';

import { Pagination } from './pagination';
import { useDataTable } from './use-data-table';
import { GlobalDataTableProps, ColumnConfig } from './types/types';

export function GlobalDataTable<T>({
  data = [],
  apiCall,
  config,
  onRowClick,
  loading: externalLoading = false,
  error: externalError = null,
  currentPage,
  itemsPerPage,
  totalItems,
  onPageChange,
  onItemsPerPageChange,
  sortField: externalSortField,
  sortOrder: externalSortOrder,
}: Readonly<GlobalDataTableProps<T>>) {
  const theme = useTheme();

  // Use data table hook
  const {
    tableData,
    loading: hookLoading,
    error: hookError,
    apiResponse,
    pagination,
    handlePageChange,
    handlePageSizeChange,
    sort,
    handleSort,
  } = useDataTable({
    data,
    apiCall,
    config,
  });

  // Use external loading/error if provided, otherwise use hook
  const loading = externalLoading || hookLoading;
  const error = externalError || hookError;

  // Use external sort state if provided (for server-side sorting), otherwise use internal sort
  const currentSort = useMemo(() => {
    if (
      config.enableServerSort &&
      externalSortField !== undefined &&
      externalSortOrder !== undefined
    ) {
      return { field: externalSortField, order: externalSortOrder };
    }
    return sort;
  }, [config.enableServerSort, externalSortField, externalSortOrder, sort]);

  // Common styles using theme - Enhanced for accessibility and older users
  const commonCellStyles = useMemo(
    () => ({
      color: theme.palette.text.primary,
      lineHeight: { xs: 1.6, sm: 1.7, md: 1.8 },
      padding: { xs: '12px 16px', sm: '14px 16px', md: '16px 16px' },
      fontWeight: 400,
      // backgroundColor removed - will inherit from TableRow
    }),
    [theme]
  );

  const headerCellStyles = useMemo(
    () => ({
      backgroundColor: '#F5F5F5',
      fontWeight: 700,
      whiteSpace: 'normal',
      borderBottom: `1px solid ${theme.palette.divider}`,
      lineHeight: { xs: 1.4, sm: 1.5, md: 1.6 },
      padding: { xs: '12px 16px', sm: '14px 16px', md: '16px 16px' },
      letterSpacing: '0.3px',
    }),
    [theme]
  );

  // Event handlers
  const handleRowClick = useCallback(
    (row: T, index: number) => {
      if (!loading) {
        onRowClick?.(row, index);
      }
    },
    [onRowClick, loading]
  );

  const handleColumnSort = useCallback(
    (column: ColumnConfig<T>) => {
      const field = column.sortField || (column.key as string);

      // If server sorting is enabled and onSortChange is provided in config, use external handler
      if (config.enableServerSort && config.onSortChange) {
        // Determine new sort order
        const currentField = currentSort.field;
        const currentOrder = currentSort.order;
        let newOrder: 'asc' | 'desc' = 'asc'; // Start with 'asc' for first click

        if (currentField === field) {
          // Toggle order if same field
          newOrder = currentOrder === 'asc' ? 'desc' : 'asc';
        }

        config.onSortChange(field, newOrder);
      } else {
        // Use internal sort handler for client-side sorting
        handleSort(field);
      }
    },
    [handleSort, currentSort.field, currentSort.order, config]
  );

  // Format date helper
  const formatDate = useCallback((timestamp: number): string => {
    return new Date(timestamp).toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  }, []);

  // Render cell content
  const renderCellContent = useCallback(
    (column: ColumnConfig<T>, row: T, index: number) => {
      const cellValue = (row as Record<string, unknown>)[column.key as string];

      if (column.render) {
        return column.render(cellValue, row, index);
      }

      const value = cellValue;

      // Auto-format dates for common fields
      if ((column.key === 'createdAt' || column.key === 'updatedAt') && typeof value === 'number') {
        return formatDate(value);
      }

      return value as ReactNode;
    },
    [formatDate]
  );

  // Render skeleton loading rows
  const renderSkeletonRows = useCallback(() => {
    return Array.from({ length: pagination.rowsPerPage }, (_, index) => (
      <TableRow key={`loading-${index}`}>
        {config.columns.map((column, colIndex) => (
          <TableCell key={column.key as string} sx={commonCellStyles}>
            <Skeleton
              variant='text'
              width={colIndex === 1 ? '80%' : '60%'}
              height={28}
              sx={{
                borderRadius: 1,
                backgroundColor: 'rgba(0, 78, 162, 0.08)',
              }}
            />
          </TableCell>
        ))}
      </TableRow>
    ));
  }, [pagination.rowsPerPage, config.columns, commonCellStyles]);

  // Render no data row
  const renderNoDataRow = useCallback(() => {
    return (
      <TableRow>
        <TableCell
          colSpan={config.columns.length}
          sx={{
            textAlign: 'center',
            py: 4,
            border: 'none',
            color: theme.palette.text.secondary,
            fontSize: { xs: '14px', sm: '16px' },
            fontWeight: 500,
          }}
        >
          データなし
        </TableCell>
      </TableRow>
    );
  }, [config.columns.length, theme.palette.text.secondary]);

  // Generate stable row key
  const getRowKey = useCallback((row: T, index: number): string => {
    // Try to use common unique identifier fields
    const rowRecord = row as Record<string, unknown>;
    if (rowRecord.id !== undefined && rowRecord.id !== null) {
      return String(rowRecord.id);
    }
    if (rowRecord._id !== undefined && rowRecord._id !== null) {
      return String(rowRecord._id);
    }
    if (rowRecord.key !== undefined && rowRecord.key !== null) {
      return String(rowRecord.key);
    }
    // Fallback to index (stable for static data, acceptable for paginated data)
    return `row-${index}`;
  }, []);

  // Render data rows
  const renderDataRows = useCallback(() => {
    return tableData.map((row, rowIndex) => {
      // Get custom row style if provided
      const customRowStyle: React.CSSProperties = config.getRowStyle?.(row, rowIndex) || {};

      // Check if background color is rgba with medium opacity (archived rows)
      const bgColor = customRowStyle.backgroundColor;
      const isArchivedBackground =
        typeof bgColor === 'string' &&
        bgColor.includes('rgba(208, 213, 221') &&
        bgColor.includes('0.5');

      // Calculate hover background color
      // For archived rows: use darker gray. For others: keep same or default
      let hoverBackgroundColor = 'rgba(0, 78, 162, 0.12)'; // Default hover
      if (isArchivedBackground) {
        // Archived rows: darker gray on hover
        hoverBackgroundColor = 'rgba(208, 213, 221, 0.85)';
      } else if (customRowStyle.backgroundColor) {
        // Selected/other custom backgrounds: keep the same
        hoverBackgroundColor = customRowStyle.backgroundColor;
      }

      return (
        <TableRow
          hover={!loading}
          tabIndex={-1}
          key={getRowKey(row, rowIndex)}
          sx={{
            cursor: onRowClick && !loading ? 'pointer' : 'default',
            transition: 'all 0.2s ease-in-out',
            opacity: loading ? 0.7 : 1,
            pointerEvents: loading ? 'none' : 'auto',
            backgroundColor: customRowStyle.backgroundColor || '#ffffff',
            ...customRowStyle,
            '&:hover': loading
              ? {}
              : {
                  backgroundColor: hoverBackgroundColor,
                  boxShadow: '0 4px 12px rgba(0, 78, 162, 0.2)',
                  transform: 'translateY(-1px)',
                  '& .MuiTableCell-root': {
                    borderBottom: `1px solid ${theme.palette.primary.main}`,
                    color: theme.palette.primary.main,
                  },
                },
          }}
          onClick={() => handleRowClick(row, rowIndex)}
        >
          {config.columns.map((column) => (
            <TableCell
              key={column.key as string}
              align={column.align || 'left'}
              className={column.className}
              sx={{
                ...commonCellStyles,
                width: column.width,
                maxWidth: column.maxWidth,
                wordWrap: 'break-word',
                whiteSpace: 'normal',
                lineHeight: 1.4,
                padding: '8px 16px',
              }}
            >
              {renderCellContent(column, row, rowIndex)}
            </TableCell>
          ))}
        </TableRow>
      );
    });
  }, [
    tableData,
    loading,
    onRowClick,
    theme.palette.primary.main,
    config.columns,
    config.getRowStyle,
    commonCellStyles,
    renderCellContent,
    handleRowClick,
    getRowKey,
  ]);

  // Get pagination props
  const getPaginationProps = useCallback(() => {
    const currentPageValue = currentPage ? currentPage - 1 : pagination.page;
    const totalPagesValue = totalItems
      ? Math.max(1, Math.ceil(totalItems / (itemsPerPage || 100)))
      : Math.max(1, pagination.totalPages);
    const rowsPerPageValue = itemsPerPage || pagination.rowsPerPage;
    const totalCountValue = totalItems ?? pagination.totalCount;
    const onPageChangeHandler = onPageChange
      ? (page: number) => onPageChange(page + 1)
      : handlePageChange;
    const onRowsPerPageChangeHandler = onItemsPerPageChange || handlePageSizeChange;
    const rowsPerPageOptionsValue = pagination.rowsPerPageOptions || [20, 50, 100];

    let apiResponseInfo;
    if (config.showApiInfo && apiResponse) {
      apiResponseInfo = {
        currentPage: apiResponse.pagination.currentPage,
        totalPages: apiResponse.pagination.totalPages,
        requestId: apiResponse.meta?.requestId,
        isLoading: loading,
      };
    } else if (loading) {
      apiResponseInfo = {
        currentPage: 1,
        totalPages: 1,
        requestId: 'loading...',
        isLoading: true,
      };
    }

    return {
      currentPage: currentPageValue,
      totalPages: totalPagesValue,
      rowsPerPage: rowsPerPageValue,
      totalCount: totalCountValue,
      onPageChange: onPageChangeHandler,
      onRowsPerPageChange: onRowsPerPageChangeHandler,
      rowsPerPageOptions: rowsPerPageOptionsValue,
      apiResponseInfo,
    };
  }, [
    currentPage,
    pagination.page,
    totalItems,
    itemsPerPage,
    pagination.totalPages,
    pagination.rowsPerPage,
    pagination.totalCount,
    onPageChange,
    handlePageChange,
    onItemsPerPageChange,
    handlePageSizeChange,
    pagination.rowsPerPageOptions,
    config.showApiInfo,
    apiResponse,
    loading,
  ]);

  // Error state
  if (error) {
    return (
      <Box
        sx={{
          width: '100vw',
          height: '100vh',
          minHeight: '400px',
          maxWidth: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 2, sm: 3, md: 4 },
          boxSizing: 'border-box',
        }}
      >
        <Alert
          severity='error'
          sx={{
            backgroundColor: `rgba(215, 8, 47, 0.1)`,
            color: theme.palette.secondary.main,
            border: `1px solid ${theme.palette.secondary.main}`,
            borderRadius: 2,
            '& .MuiAlert-icon': {
              color: theme.palette.secondary.main,
            },
          }}
        >
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: 'auto',
        height: 'auto',
        minHeight: config.height?.xs || '600px',
        maxWidth: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        boxSizing: 'border-box',
      }}
    >
      {/* Pagination - Show unless explicitly hidden */}
      {!config.hidePagination && <Pagination {...getPaginationProps()} />}

      {/* Data Table with Loading Bar */}
      <Box sx={{ position: 'relative', flex: 1 }}>
        {/* Loading Bar - Always visible when loading */}
        {loading && (
          <LinearProgress
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 10,
              height: 3,
              borderRadius: '2px 2px 0 0',
              backgroundColor: 'rgba(0, 78, 162, 0.15)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: theme.palette.primary.main,
                transition: 'transform 0.4s linear',
              },
            }}
          />
        )}

        <TableContainer
          sx={{
            minHeight: config.height || { xs: '400px', sm: '500px', md: '600px' },
            maxHeight: config.maxHeight || { xs: '70vh', sm: '75vh', md: '80vh' },
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            overflow: 'auto',
            width: '100%',
          }}
        >
          <Table stickyHeader={config.stickyHeader ?? true} aria-label='data table'>
            <TableHead>
              <TableRow>
                {config.columns.map((column) => {
                  const sortField = column.sortField || (column.key as string);
                  const isActive = currentSort.field === sortField;

                  let headerContent;
                  if (column.renderHeader) {
                    headerContent = column.renderHeader();
                  } else if (column.sortable) {
                    headerContent = (
                      <TableSortLabel
                        active={isActive}
                        direction={isActive ? currentSort.order : 'asc'}
                        onClick={() => handleColumnSort(column)}
                        sx={{
                          fontWeight: 'inherit',
                          backgroundColor: 'transparent',
                          '& .MuiTableSortLabel-icon': {
                            opacity: isActive ? 1 : 0,
                            transition: 'opacity 0.2s ease-in-out',
                          },
                          '&:hover': {},
                          '&:hover .MuiTableSortLabel-icon': {
                            opacity: 1,
                          },
                          '&.Mui-active': {
                            fontWeight: 'inherit',
                          },
                          '&.Mui-active .MuiTableSortLabel-icon': {
                            opacity: 1,
                          },
                        }}
                      >
                        {column.label}
                      </TableSortLabel>
                    );
                  } else {
                    headerContent = column.label;
                  }

                  return (
                    <TableCell
                      key={column.key as string}
                      align={column.align || 'left'}
                      sx={{
                        ...headerCellStyles,
                        ...(column.width && { width: column.width }),
                        ...(column.maxWidth && { maxWidth: column.maxWidth }),
                      }}
                    >
                      {headerContent}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody sx={{ position: 'relative' }}>
              {/* Show skeleton only when loading AND no data */}
              {loading && tableData.length === 0 && renderSkeletonRows()}
              {/* Show "no data" only when not loading AND no data */}
              {tableData.length === 0 && !loading && renderNoDataRow()}
              {/* Always show data rows if we have data, even when loading */}
              {tableData.length > 0 && renderDataRows()}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
}

export default GlobalDataTable;
